import Autocomplete from '@mui/material/Autocomplete';
import React from 'react';
import { useEnhancedAutocompleteList, useLiveMessage } from './hooks';
import StaticSearchAutocompleteListbox from './static-search-autocomplete-listbox';
import StaticSearchAutocompletePopper from './static-search-autocomplete-popper';
import { StaticSearchErrorPanel } from './static-search-error-panel';
import StaticSearchInput from './static-search-input';
import { StaticSearchLoadingPanel } from './static-search-loading-panel';
import StaticSearchNoOptionsPanel from './static-search-no-options-panel';
import type { StaticSearchErrorKind } from '../api';
import { createStaticSearchPopperMaxHeightModifier, getItemHref, stripHighlight } from '../utils';

export interface StaticSearchAutocompleteProps {
    hasMore: boolean;
    hasSearched: boolean;
    isLoading: boolean;
    isLoadingMore: boolean;
    loadMoreError: boolean;
    loadMoreErrorVariant: 'connection' | 'generic';
    onChange: (value: string) => void;
    onLoadMore: () => Promise<void>;
    options: readonly Search.Static[];
    searchError: StaticSearchErrorKind | null;
    value: string;
}

const renderItem = (props, option) => {
    const { content, title } = option.doc;
    const href = getItemHref(option);
    return (
        <li
            {...props}
            aria-label={`${stripHighlight(title)}. ${stripHighlight(content)}`}
            id={option.id}
            style={{
                borderTop: '1px solid #e0e0e0',
                boxSizing: 'border-box',
                padding: '8px 16px',
            }}
        >
            <a aria-hidden="true" className="StaticSearchOption" href={href} tabIndex={-1}>
                <span className="StaticSearchOption-header" dangerouslySetInnerHTML={{ __html: title }} />
                <span className="StaticSearchOption-content" dangerouslySetInnerHTML={{ __html: content }} />
            </a>
        </li>
    );
};

const getNoResults = (value, isLoading, searchError, options, hasSearched) => {
    return value.trim().length >= 3 && !isLoading && searchError === null && options.length === 0 && hasSearched;
};

function StaticSearchAutocomplete({
    hasMore,
    hasSearched,
    isLoading,
    isLoadingMore,
    loadMoreError,
    loadMoreErrorVariant,
    onChange,
    onLoadMore,
    options,
    searchError,
    value,
}: StaticSearchAutocompleteProps) {
    const autocompleteRootRef = React.useRef<HTMLDivElement | null>(null);
    const [closedByUser, setClosedByUser] = React.useState(false);
    const [focusedItemLive, setFocusedItemLive] = React.useState('');
    const [, syncPopperLayout] = React.useReducer((version: number) => version + 1, 0);
    const { highlightIndex, listboxRef, setHighlightIndex } = useEnhancedAutocompleteList(options);

    const hasClearableText = value.trim().length > 0;
    const noResults = getNoResults(value, isLoading, searchError, options, hasSearched);
    const liveMessage = useLiveMessage(value, isLoading, isLoadingMore, searchError, options, focusedItemLive);

    const open =
        !closedByUser &&
        value.trim().length >= 3 &&
        (isLoading || options.length > 0 || searchError !== null || noResults);

    const hasAutocompleteOptions = options.length > 0;

    const popperStateClass = React.useMemo(() => {
        if (hasAutocompleteOptions) {
            return undefined;
        }
        if (searchError) {
            return 'StaticSearch-popper--error';
        }
        return 'StaticSearch-popper--noResults';
    }, [hasAutocompleteOptions, searchError]);

    const popperSlotProps = React.useMemo(
        () => ({
            className: popperStateClass,
            'data-static-search-autocomplete': 'true',
            disablePortal: true,
            modifiers: [
                {
                    enabled: false,
                    name: 'flip',
                },
                createStaticSearchPopperMaxHeightModifier(hasAutocompleteOptions),
            ],
            placement: 'bottom-start' as const,
            sx: { zIndex: 9999999 },
        }),
        [hasAutocompleteOptions, popperStateClass],
    );

    const noOptionsPanel = React.useMemo(() => {
        if (searchError === 'connection') {
            return <StaticSearchErrorPanel variant="connection" />;
        }
        if (searchError === 'generic') {
            return <StaticSearchErrorPanel variant="generic" />;
        }
        return <StaticSearchNoOptionsPanel />;
    }, [searchError]);

    React.useEffect(() => {
        setClosedByUser(false);
    }, [value]);

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }

        const root = autocompleteRootRef.current;
        if (!root || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            syncPopperLayout();
        });
        observer.observe(root);

        return () => {
            observer.disconnect();
        };
    }, [open]);

    const loadMore = React.useCallback(() => {
        if (isLoadingMore || !hasMore) return;
        onLoadMore();
    }, [hasMore, isLoadingMore, onLoadMore]);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'ArrowDown' && highlightIndex === options.length - 1) {
                event.stopPropagation();
                if (isLoadingMore) {
                    event.preventDefault();
                    return;
                }
                if (hasMore && !loadMoreError) {
                    loadMore();
                }
            }
            if (event.key === 'ArrowUp' && highlightIndex === 0) {
                event.stopPropagation();
            }
            if (event.key === 'Enter') {
                const { id } = options[highlightIndex];
                window.location.href = `/${id}`;
            }
        },
        [hasMore, highlightIndex, isLoadingMore, loadMore, loadMoreError, options],
    );

    const renderInput = React.useMemo(
        () => (params: Parameters<typeof StaticSearchInput>[0]) =>
            StaticSearchInput({
                ...params,
                clearSearch: () => {
                    onChange('');
                    setClosedByUser(false);
                },
                handleKeyDown,
                hasClearableText,
                inputValue: value,
                onFocus: () => {
                    if (value.trim().length >= 3) {
                        setClosedByUser(false);
                    }
                },
            }),
        [handleKeyDown, hasClearableText, onChange, value],
    );

    return (
        <>
            <div
                aria-atomic="true"
                aria-live="polite"
                className="StaticSearch-srLive"
                data-seleniumid="static-search-focused-item-live"
                style={{
                    border: 0,
                    clip: 'rect(0 0 0 0)',
                    height: 1,
                    margin: -1,
                    overflow: 'hidden',
                    padding: 0,
                    position: 'absolute',
                    whiteSpace: 'nowrap',
                    width: 1,
                }}
            >
                {liveMessage}
            </div>

            <Autocomplete
                ref={autocompleteRootRef}
                clearOnBlur={false}
                filterOptions={x => x}
                forcePopupIcon={false}
                getOptionKey={option => option.id}
                getOptionLabel={() => ''}
                inputValue={value}
                loading={isLoading}
                loadingText={<StaticSearchLoadingPanel />}
                noOptionsText={noOptionsPanel}
                onClose={(_, reason) => {
                    if (reason !== 'selectOption') {
                        setClosedByUser(true);
                    }
                }}
                onHighlightChange={(_, option, reason) => {
                    const newIndex = options.indexOf(option);
                    setHighlightIndex(newIndex);
                    if (newIndex === options.length - 1 && hasMore) {
                        const listbox = listboxRef.current;
                        if (listbox) {
                            listbox.scrollTop = listbox.scrollHeight;
                        }
                    }
                    if (reason === 'keyboard') {
                        setFocusedItemLive(
                            option ? `${stripHighlight(option.doc.title)}. ${stripHighlight(option.doc.content)}` : '',
                        );
                    }
                }}
                onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'reset') return;
                    onChange(newInputValue);
                }}
                open={open}
                options={options}
                renderInput={renderInput}
                renderOption={renderItem}
                slotProps={{
                    listbox: {
                        hasMore,
                        isLastItemFocused: highlightIndex === options.length - 1,
                        isLoadingMore,
                        loadMoreError,
                        loadMoreErrorVariant,
                        onLoadMore: loadMore,
                        ref: listboxRef,
                    } as any,
                    popper: popperSlotProps,
                }}
                slots={{ listbox: StaticSearchAutocompleteListbox, popper: StaticSearchAutocompletePopper }}
                sx={{ alignSelf: 'stretch', minWidth: 0, width: '100%' }}
                value={null}
            />
        </>
    );
}

export default StaticSearchAutocomplete;
